import { Feature } from 'ol';
import { Style } from 'ol/style';
import type { ArcGISFeatureLayer as GISServiceFeatureLayer, ArcGISWebMap, MapLayerLegendInfo } from './gis-service-list';
import { ARCGIS_CONSTANTS } from '../constants/arcgis-constants';

export const ESRI_SYMBOL_TYPES = {
    SIMPLE_MARKER: 'esriSMS',
    SIMPLE_LINE: 'esriSLS',
    SIMPLE_FILL: 'esriSFS',
    PICTURE_MARKER: 'esriPMS',
    PICTURE_FILL: 'esriPFS',
    TEXT: 'esriTS',
    CARTOGRAPHIC_LINE: 'esriCLS',
    CIM_SYMBOL_REFERENCE: 'CIMSymbolReference',
} as const;

export const CIM_LAYER_TYPES = {
    SOLID_STROKE: 'CIMSolidStroke',
    SOLID_FILL: 'CIMSolidFill',
} as const;

export const GEOJSON_GEOMETRY_TYPES = {
    POINT: 'Point',
    LINE_STRING: 'LineString',
    POLYGON: 'Polygon',
    MULTI_POINT: 'MultiPoint',
    MULTI_LINE_STRING: 'MultiLineString',
    MULTI_POLYGON: 'MultiPolygon',
} as const;

export const ESRI_GEOMETRY_TYPES = {
    POINT: 'esriGeometryPoint',
    POLYLINE: 'esriGeometryPolyline',
    POLYGON: 'esriGeometryPolygon',
} as const;

export const ESRI_RENDERER_TYPES = {
    SIMPLE: 'simple',
    UNIQUE_VALUE: 'uniqueValue',
    CLASS_BREAKS: 'classBreaks',
    HEATMAP: 'heatmap',
    DOT_DENSITY: 'dotDensity',
} as const;

export const ESRI_LINE_STYLES = {
    SOLID: 'esriSLSSolid',
    DASH: 'esriSLSDash',
    DOT: 'esriSLSDot',
    DASH_DOT: 'esriSLSDashDot',
    DASH_DOT_DOT: 'esriSLSDashDotDot',
} as const;

export const ESRI_FILL_STYLES = {
    SOLID: 'esriSFSSolid',
    NULL: 'esriSFSNull',
    HOLLOW: 'esriSFSHollow',
    BACKWARD_DIAGONAL: 'esriSFSBackwardDiagonal',
    FORWARD_DIAGONAL: 'esriSFSForwardDiagonal',
    HORIZONTAL: 'esriSFSHorizontal',
    VERTICAL: 'esriSFSVertical',
    CROSS: 'esriSFSCross',
    DIAGONAL_CROSS: 'esriSFSDiagonalCross',
} as const;

export const ESRI_MARKER_STYLES = {
    CIRCLE: 'esriSMSCircle',
    SQUARE: 'esriSMSSquare',
    DIAMOND: 'esriSMSDiamond',
    CROSS: 'esriSMSCross',
    X: 'esriSMSX',
} as const;

export interface IconConfig {
    src: string;
    size: [number, number];
    offset?: [number, number];
    rotation?: number;
}

export interface ArcGISExtent {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference?: ArcGISSpatialReference;
}

export interface ArcGISSpatialReference {
    wkid?: number;
    latestWkid?: number;
    wkt?: string;
    wkt2?: string;
}

export interface SpatialGridCell {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
}

export interface ArcGISFeature {
    attributes: Record<string, string | number | boolean | null | unknown>;
    geometry?: ArcGISGeometry;
}

export interface ArcGISGeometry {
    x?: number;
    y?: number;
    z?: number;
    m?: number;
    points?: number[][];
    paths?: number[][][];
    rings?: number[][][];
    spatialReference?: ArcGISSpatialReference;
}

export interface ArcGISRenderer {
    type: 'simple' | 'uniqueValue' | 'classBreaks' | 'heatmap' | 'dotDensity';
    symbol?: ArcGISSymbol;
    defaultSymbol?: ArcGISSymbol;
    field?: string;
    field1?: string;
    field2?: string;
    field3?: string;
    fieldDelimiter?: string;
    defaultLabel?: string;
    uniqueValueInfos?: ArcGISUniqueValueInfo[];
    classBreakInfos?: ArcGISClassBreakInfo[];
    visualVariables?: ArcGISVisualVariable[];
    authoringInfo?: Record<string, unknown>;
    legendOptions?: Record<string, unknown>;
    blurRadius?: number;
    radius?: number;
    colorStops?: Array<{ ratio: number; color: number[] }>;
    dotValue?: number;
    dotSize?: number;
    attributes?: Array<{ field: string; label: string; color?: number[] }>;
}

export interface ArcGISUniqueValueInfo {
    value: string | number;
    symbol: ArcGISSymbol;
    label?: string;
    description?: string;
}

export interface ArcGISClassBreakInfo {
    minValue: number;
    maxValue: number;
    classMinValue?: number;
    classMaxValue?: number;
    symbol: ArcGISSymbol;
    label?: string;
    description?: string;
}

export interface ArcGISSymbol {
    type: 'esriSMS' | 'esriSLS' | 'esriSFS' | 'esriPMS' | 'esriPFS' | 'esriTS' | 'esriCLS' | 'CIMSymbolReference';
    color?: number[];
    size?: number;
    width?: number;
    height?: number;
    angle?: number;
    xoffset?: number;
    yoffset?: number;
    xscale?: number;
    yscale?: number;
    style?: string;
    imageData?: string;
    contentType?: string;
    url?: string;
    outline?: ArcGISOutline;
    font?: ArcGISFont;
    text?: string;
    haloColor?: number[];
    haloSize?: number;
    horizontalAlignment?: 'left' | 'right' | 'center' | 'justify';
    verticalAlignment?: 'baseline' | 'top' | 'middle' | 'bottom';
    cap?: string;
    join?: string;
    miterLimit?: number;
    marker?: ArcGISMarker;
    data?: CIMSymbolReference;
    symbol?: CIMSymbol;
}

export interface ArcGISOutline {
    color?: number[];
    width?: number;
    style?: string;
    type?: string;
}

export interface ArcGISFont {
    family?: string;
    size?: number;
    style?: 'normal' | 'italic';
    weight?: 'normal' | 'bold';
    decoration?: 'none' | 'underline' | 'line-through';
}

export interface ArcGISMarker {
    type?: string;
    style?: string;
    placement?: 'begin' | 'end' | 'begin-end';
    color?: number[];
    size?: number;
}

export interface CIMSymbolReference {
    type: 'CIMSymbolReference';
    symbol?: CIMSymbol;
}

export interface CIMSymbol {
    type: string;
    symbolLayers?: CIMSymbolLayer[];
}

export interface CIMSymbolLayer {
    type: string;
    enable?: boolean;
    colorLocked?: boolean;
    size?: number;
    width?: number;
    color?: number[];
    markerGraphics?: CIMMarkerGraphic[];
    [key: string]: unknown;
}

export interface CIMMarkerGraphic {
    type: string;
    geometry?: ArcGISGeometry;
    symbol?: CIMSymbolReference;
}

export interface ArcGISVisualVariable {
    type: 'sizeInfo' | 'colorInfo' | 'rotationInfo' | 'transparencyInfo';
    field?: string;
    valueExpression?: string;
    valueExpressionTitle?: string;
    minSize?: number;
    maxSize?: number;
    minDataValue?: number;
    maxDataValue?: number;
    stops?: ArcGISStop[];
    legendOptions?: Record<string, unknown>;
}

export interface ArcGISStop {
    value: number;
    size?: number;
    color?: number[];
    transparency?: number;
    label?: string;
}

export interface ArcGISDrawingInfo {
    renderer: ArcGISRenderer;
    transparency?: number;
    showLabels?: boolean;
    labelingInfo?: ArcGISLabelingInfo[];
    scaleSymbols?: boolean;
}

export interface ArcGISLabelExpressionInfo {
    expression?: string;
    value?: string;
}

export interface ArcGISLabelingInfo {
    labelExpression?: string;
    labelExpressionInfo?: ArcGISLabelExpressionInfo;
    labelPlacement?: string;
    symbol?: ArcGISSymbol;
    minScale?: number;
    maxScale?: number;
    where?: string;
}

export interface ArcGISTreeNode {
    name: string;
    checked: boolean;
    showLegend: boolean;
    selection: boolean;
    level: number;
    isArcGIS: boolean;
    id: string;
    layerData?: ArcGISLayerData | GISServiceFeatureLayer | ArcGISWebMap | null;
    child?: ArcGISTreeNode[];
    parentId?: string;
    assetType?: 'feature-service' | 'web-map' | 'map-service';
    opacity?: number;
    visible?: boolean;
    legendInfo?: MapLayerLegendInfo[];
    showLabels?: boolean;
    labelField?: string;
}

export interface ArcGISLayerData {
    id: number | string;
    name: string;
    geometryType?: string;
    serviceUrl: string;
    url?: string;
    visibility?: boolean;
    opacity?: number;
    title?: string;
    layerType?: string;
    itemId?: string;
    webMapId?: string;
    layerPositionInAsset?: number;
    totalLayersInAsset?: number;
    assetIndex?: number;
    minScale?: number;
    maxScale?: number;
    labelField?: string;
    showLabels?: boolean;
    labelMinScale?: number;
    labelMaxScale?: number;
}

export interface ArcGISQueryParams {
    token?: string;
    f?: string;
    where?: string;
    outFields?: string;
    returnGeometry?: string;
    outSR?: string;
    inSR?: string;
    resultRecordCount?: string;
    resultOffset?: string;
    geometry?: string;
    geometryType?: string;
    spatialRel?: string;
    returnCountOnly?: string;
    returnIdsOnly?: string;
    returnExtentOnly?: string;
    orderByFields?: string;
    groupByFieldsForStatistics?: string;
    outStatistics?: string;
    returnZ?: string;
    returnM?: string;
    returnDistinctValues?: string;
    returnTrueCurves?: string;
    resultType?: string;
    quantizationParameters?: string;
    maxAllowableOffset?: string;
}

export interface QuantizationParameters {
    mode: 'view' | 'edit';
    originPosition: 'upperLeft' | 'lowerLeft';
    tolerance: number;
    extent: ArcGISExtent;
}

export interface ArcGISOperationalLayer {
    id: string;
    title: string;
    url: string;
    layerType: string;
    visibility: boolean;
    opacity: number;
    itemId?: string;
    layerDefinition?: ArcGISLayerDefinition;
    popupInfo?: Record<string, unknown>;
    minScale?: number;
    maxScale?: number;
    featureCollection?: Record<string, unknown>;
}

export interface ArcGISLayerDefinition {
    drawingInfo?: ArcGISDrawingInfo;
    definitionExpression?: string;
    minScale?: number;
    maxScale?: number;
}

export interface ArcGISWebMapData {
    operationalLayers: ArcGISOperationalLayer[];
    baseMap?: ArcGISBaseMap;
    spatialReference?: ArcGISSpatialReference;
    version?: string;
    authoringApp?: string;
    authoringAppVersion?: string;
}

export interface ArcGISBaseMap {
    baseMapLayers: ArcGISBaseMapLayer[];
    title: string;
}

export interface ArcGISBaseMapLayer {
    id: string;
    url: string;
    layerType: string;
    visibility: boolean;
    opacity: number;
    title?: string;
}

export interface ArcGISFeatureSet {
    features: ArcGISFeature[];
    fields?: ArcGISField[];
    geometryType?: string;
    spatialReference?: ArcGISSpatialReference;
    exceededTransferLimit?: boolean;
    displayFieldName?: string;
    fieldAliases?: Record<string, string>;
}

export interface ArcGISField {
    name: string;
    type: 'esriFieldTypeOID' | 'esriFieldTypeString' | 'esriFieldTypeInteger' | 'esriFieldTypeDouble' |
          'esriFieldTypeDate' | 'esriFieldTypeDateOnly' | 'esriFieldTypeTimeOnly' | 'esriFieldTypeBigInteger' |
          'esriFieldTypeGUID' | 'esriFieldTypeGeometry' | 'esriFieldTypeSingle' | 'esriFieldTypeSmallInteger';
    alias?: string;
    domain?: ArcGISDomain;
    editable?: boolean;
    nullable?: boolean;
    length?: number;
    defaultValue?: unknown;
}

export interface ArcGISDomain {
    type: 'codedValue' | 'range' | 'inherited';
    name?: string;
    codedValues?: Array<{
        name: string;
        code: string | number;
    }>;
    range?: [number, number];
}

export interface ArcGISLayerMetadata {
    id: number;
    name: string;
    type: string;
    geometryType: string;
    description?: string;
    copyrightText?: string;
    parentLayer?: { id: number };
    subLayers?: Array<{ id: number }>;
    minScale?: number;
    maxScale?: number;
    defaultVisibility?: boolean;
    extent?: ArcGISExtent;
    hasAttachments?: boolean;
    htmlPopupType?: string;
    displayField?: string;
    typeIdField?: string;
    subtypeFieldName?: string;
    subtypeField?: string;
    defaultSubtypeCode?: number;
    fields?: ArcGISField[];
    indexes?: Array<{
        name: string;
        fields: string;
        isAscending: boolean;
        isUnique: boolean;
        description?: string;
    }>;
    relationships?: Array<{
        id: number;
        name: string;
        relatedTableId: number;
        role: string;
        cardinality: string;
        keyField: string;
        composite: boolean;
    }>;
    capabilities?: string;
    maxRecordCount?: number;
    tileMaxRecordCount?: number;
    standardMaxRecordCount?: number;
    supportsStatistics?: boolean;
    supportsAdvancedQueries?: boolean;
    supportedQueryFormats?: string;
    isDataVersioned?: boolean;
    ownershipBasedAccessControlForFeatures?: {
        allowOthersToQuery?: boolean;
        allowOthersToDelete?: boolean;
        allowOthersToUpdate?: boolean;
    };
    useStandardizedQueries?: boolean;
    advancedQueryCapabilities?: {
        useStandardizedQueries?: boolean;
        supportsStatistics?: boolean;
        supportsOrderBy?: boolean;
        supportsDistinct?: boolean;
        supportsPagination?: boolean;
        supportsTrueCurve?: boolean;
        supportsCoordinatesQuantization?: boolean;
    };
    dateFieldsTimeReference?: {
        timeZone?: string;
        respectsDaylightSaving?: boolean;
    };
    drawingInfo?: ArcGISDrawingInfo;
}

export interface ArcGISServiceMetadata {
    currentVersion: number;
    serviceDescription?: string;
    hasVersionedData?: boolean;
    hasArchivedData?: boolean;
    supportsDisconnectedEditing?: boolean;
    supportedQueryFormats?: string;
    maxRecordCount?: number;
    capabilities?: string;
    description?: string;
    copyrightText?: string;
    spatialReference?: ArcGISSpatialReference;
    initialExtent?: ArcGISExtent;
    fullExtent?: ArcGISExtent;
    units?: string;
    documentInfo?: {
        Title?: string;
        Author?: string;
        Comments?: string;
        Subject?: string;
        Category?: string;
        Keywords?: string;
    };
    layers: Array<{
        id: number;
        name: string;
        parentLayerId: number;
        defaultVisibility: boolean;
        subLayerIds?: number[] | null;
        minScale: number;
        maxScale: number;
        geometryType?: string;
        type?: string;
    }>;
    tables: Array<{
        id: number;
        name: string;
    }>;
}

export type StyleFunction = (feature: Feature) => Style | Style[];

export interface ArcGISFeatureLayer {
    id: string;
    name: string;
    geometryType: string;
    serviceUrl: string;
    url?: string;
    webMapId?: string;
    visibility?: boolean;
    opacity?: number;
    title?: string;
    layerType?: string;
    itemId?: string;
}

export interface AssetItem {
    id: string;
    name: string;
    type: 'feature-service' | 'web-map' | 'map-service';
    typeLabel: string;
    url?: string;
    selected: boolean;
    owner?: string;
    modified?: number;
    layers?: ArcGISFeatureLayer[];
    access?: string;
    contentStatus?: string;
    origin?: string;
}

export interface AssetsSelectionEvent {
    assets: AssetItem[];
    autoPlot: boolean;
}

export interface ArcGISSelectionState {
    customerId: number;
    arcgisUsername: string;
    timestamp: string;
    selectedAssets: SelectedAsset[];
    layerTreeState: LayerTreeState[];
}

export interface SelectedAsset {
    id: string;
    name: string;
    type: 'feature-service' | 'web-map' | 'map-service';
    url: string;
    owner?: string;
    modified?: number;
    access?: string;
    contentStatus?: string;
    origin?: string;
}

export interface LayerTreeState {
    assetId: string;
    assetName: string;
    assetType: 'feature-service' | 'web-map' | 'map-service';
    layers: LayerState[];
}

export interface LayerState {
    id: string;
    name: string;
    url: string;
    checked: boolean;
    opacity?: number;
    showLabels?: boolean;
}

export type ServiceAuthType = 'none' | 'same-portal' | 'external';

export interface ServiceAuthInfo {
    requiresToken: boolean;
    authType: ServiceAuthType;
    reason: string;
}

export interface ServiceTokenContext {
    serviceUrl: string;
    access?: string;
    contentStatus?: string;
    origin?: string;
}

export type FetchStrategy = typeof ARCGIS_CONSTANTS.FETCH_STRATEGY[keyof typeof ARCGIS_CONSTANTS.FETCH_STRATEGY];

export interface PBFCapabilities {
    supportsPBF: boolean;
    supportsQuantization: boolean;
    maxRecordCount: number;
    tileMaxRecordCount?: number;
    standardMaxRecordCount?: number;
}

export interface GeoJSONFeatureCollection {
    type: 'FeatureCollection';
    features: GeoJSONFeature[];
}

export interface GeoJSONFeature {
    type: 'Feature';
    geometry: GeoJSONGeometry | null;
    properties: Record<string, string | number | boolean | null | unknown>;
    id?: string | number;
}

export interface GeoJSONGeometry {
    type: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon';
    coordinates: number[] | number[][] | number[][][] | number[][][][];
}

export interface PBFParseResult {
    featureCollection: GeoJSONFeatureCollection;
    exceededTransferLimit: boolean;
}

export interface ArcGISIdentifyParams {
    geometry: string;
    geometryType: string;
    sr: string;
    layers: string;
    tolerance: string;
    mapExtent: string;
    imageDisplay: string;
    returnGeometry: string;
    f: string;
}

export interface ArcGISIdentifyResult {
    layerId: number;
    layerName: string;
    displayFieldName: string;
    value: string;
    attributes: Record<string, string | number | boolean | null>;
    geometryType: string;
    geometry: ArcGISGeometry;
}

export interface ArcGISIdentifyResponse {
    results: ArcGISIdentifyResult[];
}

